import React, {useEffect, useRef, useState} from 'react';
import {Link, useHistory, useParams} from 'react-router-dom';
import ReactSelect from 'react-select';

import {Editor} from 'react-draft-wysiwyg';
import draftToHtml from 'draftjs-to-html';
import {convertToRaw, EditorState, ContentState} from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

import {Flow, FlowFetchResponse} from '../_services/Flow';
import {SortableColumnState, Form as FormType, InboxData} from '../types';
import {LoadingIndicator} from '../components/LoadingIndicator';
import {DataTable} from '../components/DataTable';
import {cellRenderers} from '../_helpers/datatable-cellrenderers';
import {Paginator} from '../components/Paginator';
import {Alert} from '../components/Alert';
import {Modal} from '../components/Modal';

const getInitialEditorStateFromHtml = (htmlContent: string) => {
    const contentBlock = htmlToDraft(htmlContent || '');
    let initialEditorState = undefined;
    if (contentBlock) {
        const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
        initialEditorState = EditorState.createWithContent(contentState)
    } else {
        initialEditorState = EditorState.createEmpty()
    }
    return initialEditorState;
};

const initialMyRequestsSearchFilters = {
    show_step_assignees: true,
    fieldFilters: [
        {
            workflowField: 'workflow_final_status',
            workflowFieldOperator: 'isnot',
            workflowFieldSearchValueOption: [
                {
                    value: 'complete',
                    label: 'Complete'
                }
            ]
        }
    ],
    page: 1,
    page_size: 10
};

const initialAddResourceModalState = {show: false, resource: {}, resourceIndex: -1};

type WorkflowHomeProps = {};

export const WorkflowHome: React.FunctionComponent<WorkflowHomeProps> = ({}) => {
    const {workflowId} = useParams();
    const history = useHistory();

    const [pageError, setPageError] = useState<string>('');
    const [isSavingWorkflowHome, setIsSavingWorkflowHome] = useState<boolean>(false);

    const [pageIsLoading, setPageIsLoading] = useState<boolean>(true);
    const [form, setForm] = useState<FormType>();

    const [inboxIsLoading, setInboxIsLoading] = useState<boolean>(false);
    const [inbox, setInbox] = useState<InboxData>();
    const [inboxSort, setInboxSort] = useState<SortableColumnState>();

    const [myRequestsIsLoading, setMyRequestsIsLoading] = useState<boolean>(false);
    const [myRequests, setMyRequests] = useState();
    const [myRequestsSearchFilters, setMyRequestsSearchFilters] = useState<any>({workflow: workflowId, ...initialMyRequestsSearchFilters});

    const [activeTab, setActiveTab] = useState('');

    const [descriptionEditorState, setDescriptionEditorState] = useState<any>({content: null, show: false, isSaving: false})
    const showDescriptionEditor = (shouldShow: boolean) => {
        if (!form?.userIsWfo) {
            return;
        }

        setDescriptionEditorState((state: any) => ({
            ...state,
            show: shouldShow,
            content: shouldShow ? getInitialEditorStateFromHtml(form?.workflowHome?.description || '') : null
        }))
    }
    const descriptionEditorRef = useRef(null);

    const [helpModalState, setHelpModalState] = useState<any>({show: false});
    const showHelpModal = (shouldShow = true) => {
        setHelpModalState(state => ({
            ...state,
            show: shouldShow
        }))
    };

    const [addResourceModalState, setAddResourceModalState] = useState<any>({...initialAddResourceModalState});

    useEffect(() => {
        setPageIsLoading(true);
        setPageError('');

        Flow.getInstance().getForm(workflowId)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {
                    setForm(response.json?.form);
                } else {
                    throw Error(response?.json?.message || 'Error loading workflow');
                }
            })
            .catch((error: Error) => {
                setPageError(error?.message || 'Error Loading Page. Please refresh and try again');
            })
            .finally(() => {
                setPageIsLoading(false);
            });

        // todo - update to use the custom column order (if on the workflow)
        setInboxIsLoading(true);
        Flow.getInstance().getInboxEntries(workflowId)
            .then((response: FlowFetchResponse) => {
                if (response.ok && response.json) {
                    setInboxIsLoading(false);
                    setInbox(response.json);
                    if (!activeTab) {
                        setActiveTab('inbox');
                    }
                } else {
                    throw Error(response?.json?.message || 'Error loading Inbox');
                }
            })
            .catch((error: any) => {
            })
            .finally(() => {
                setInboxIsLoading(false);
            })
    }, [workflowId]);

    useEffect(() => {
        setMyRequestsIsLoading(true);
        Flow.getInstance().search(true, myRequestsSearchFilters)
            .then((response: FlowFetchResponse) => {
                if (response.ok && response.json) {
                    setInboxIsLoading(false);
                    setMyRequests(response.json);
                    setActiveTab('my-requests');
                } else {
                    throw Error(response?.json?.message || 'Error loading My Requests');
                }
                setInboxIsLoading(false);
            })
            .catch((error: any) => {
            })
            .finally(() => {
                setMyRequestsIsLoading(false);
            })
    }, [workflowId, myRequestsSearchFilters]);

    if (pageIsLoading) {
        return <LoadingIndicator/>;
    }

    // todo - handle better
    if (!form) {
        return <></>;
    }

    const sortInboxEntries = (first: any, second: any) => {
        if (!inboxSort) return 0;
        if (!first.hasOwnProperty(inboxSort?.column) || !second.hasOwnProperty(inboxSort?.column)) return 0;

        return (inboxSort.direction === 'asc') ?
            first[inboxSort.column].localeCompare(second[inboxSort.column])
            :
            second[inboxSort.column].localeCompare(first[inboxSort.column]);
    };

    const saveWorkflowHome = (data: any) => {
        setPageError('');
        setIsSavingWorkflowHome(true);
        Flow.getInstance().saveWorkflowHome(form.id, data)
            .then((response: FlowFetchResponse) => {
                if (response.ok) {

                } else {
                    throw Error(response?.json?.message);
                }
            })
            .catch(error => {
                setPageError(error?.message || 'Error Saving. Please refresh and try again');
            })
            .finally(() => {
                setForm((state: any) => ({
                    ...state,
                    workflowHome: data
                }))
                setIsSavingWorkflowHome(false);
            })
    }
    const handleSaveResource = (resource, idx) => {
        setPageError('');

        saveWorkflowHome({
            ...form.workflowHome,
            resources: [
                ...form.workflowHome?.resources?.slice(0, idx),
                resource,
                ...form.workflowHome?.resources?.slice(idx + 1),
            ]
        });

        setAddResourceModalState({...initialAddResourceModalState});
    }

    const handleSaveWorkflowHomeDescription = () => {
        const rawValue = convertToRaw(descriptionEditorState.content.getCurrentContent());
        let value = draftToHtml(rawValue);
        if (value === '<p></p>') {
            value = '';
        }

        // save the description
        saveWorkflowHome({
            ...form.workflowHome,
            description: value
        });

        showDescriptionEditor(false);
    }

    return (
        <>
            {pageError && <Alert type={'danger'} message={pageError} icon={'exclamation-triangle fa-2x mr-2'}/>}
            <div className={'d-flex flex-column'}>
                <h1 className={'abb-header'}>{form.title}</h1>
                {form.description && <div className={'mt-2 mb-2'}>{form.description}</div>}
            </div>

            {descriptionEditorState?.show ?
                <>
                    <Editor
                        ref={descriptionEditorRef}
                        editorState={descriptionEditorState.content}
                        toolbarClassName="descriptionEditorToolbar"
                        wrapperClassName="descriptionEditorWrapper"
                        editorClassName="descriptionEditor"
                        onEditorStateChange={(editorState) => {
                            setDescriptionEditorState(state => ({
                                ...state,
                                content: editorState
                            }))
                        }}
                        toolbar={{
                            options: [
                                'inline', 'fontSize', 'list',
                                'textAlign', 'emoji', 'history'
                            ],
                        }}
                        toolbarCustomButtons={[
                            <div style={{height: '30px', gap: '8px'}} className={'ml-auto d-flex'}>
                                <button
                                    type={'button'}
                                    className={'btn btn-secondary btn-sm'}
                                    disabled={descriptionEditorState.isSaving}
                                    onClick={() => showDescriptionEditor(false)}
                                >Cancel
                                </button>
                                <button
                                    type={'button'}
                                    className={'btn btn-primary btn-sm'}
                                    disabled={descriptionEditorState.isSaving}
                                    onClick={handleSaveWorkflowHomeDescription}
                                >Save
                                </button>
                            </div>
                        ]}
                    />
                </>
                :
                <div className={'mt-2 mb-2'}>
                    {form?.workflowHome?.description && <div dangerouslySetInnerHTML={{__html: form.workflowHome.description}}/>}
                    {!form?.workflowHome?.description && form?.userIsWfo &&
                        <div
                            className={'d-flex justify-content-center align-items-center w-100'}
                            style={{height: '100px', border: '2px dashed var(--abb-gray-stroke', cursor: 'pointer'}}
                            onClick={() => showDescriptionEditor(true)}
                        >
                            Click to add a description
                        </div>
                    }
                    {form?.workflowHome?.description && form?.userIsWfo &&
                        <div className={'d-flex justify-content-sart'}>
                            <button
                                type={'button'}
                                className={'btn btn-link btn-sm'}
                                style={{color: 'var(--abb-secondary-text'}}
                                disabled={isSavingWorkflowHome}
                                onClick={() => showDescriptionEditor(true)}
                            >Edit
                            </button>
                        </div>
                    }
                </div>
            }

            <div className={'row'}>
                <div className={'col-8'}>
                    {form?.businessOwners?.length > 0 &&
                        <WorkflowOwnerCard owner={form.businessOwners[0]}/>
                    }
                </div>
                <div className={'col-4 workflow-resources text-right'}>
                    <h4>Resources</h4>
                    <div className="list-group list-group-flush">
                        {/*
                        <button
                            type={'button'}
                            className="list-group-item text-right"
                            onClick={() => showHelpModal(true)}
                            style={{cursor: 'pointer'}}
                        >
                            <span>Help</span>
                            <i className="far fa-question-circle ml-1"/>
                        </button>
                        */}

                        {form?.workflowHome?.resources?.map((resource, idx) => (
                            <div className={'list-group-item-container d-flex justify-content-end align-items-center'}>
                                {form?.userIsWfo &&
                                    <div className={'mr-5 flex-row wfo-actions'} style={{gap: '12px'}}>
                                        <button
                                            type={'button'}
                                            className={'btn btn-link'}
                                            disabled={isSavingWorkflowHome}
                                            onClick={() => {
                                                saveWorkflowHome({
                                                    ...form.workflowHome,
                                                    resources: [
                                                        ...form?.workflowHome?.resources?.slice(0, idx),
                                                        ...form?.workflowHome?.resources?.slice(idx + 1)
                                                    ]
                                                });
                                            }}
                                        >
                                            <i className="fas fa-times" style={{color: 'var(--abb-blue-primary)'}}/>
                                        </button>
                                        <button
                                            type={'button'}
                                            className={'btn btn-link'}
                                            disabled={isSavingWorkflowHome}
                                            onClick={() => {
                                                setAddResourceModalState(state => ({
                                                    ...state,
                                                    show: true,
                                                    resource,
                                                    resourceIndex: idx
                                                }))
                                            }}
                                        >
                                            <i className="fas fa-pencil-alt" style={{color: 'var(--abb-blue-primary)'}}/>
                                        </button>
                                        <button
                                            type={'button'}
                                            className={'btn btn-link'}
                                            disabled={idx === 0 || idx >= form.workflowHome.resources.length || isSavingWorkflowHome}
                                            onClick={() => {
                                                saveWorkflowHome({
                                                    ...form.workflowHome,
                                                    resources: [
                                                        ...form?.workflowHome?.resources?.slice(0, idx - 1),
                                                        form.workflowHome.resources[idx],
                                                        form.workflowHome.resources[idx - 1],
                                                        ...form?.workflowHome?.resources?.slice(idx + 1)
                                                    ]
                                                });
                                            }}
                                        >
                                            <i className="fas fa-arrow-up" style={{color: 'var(--abb-blue-primary)'}}/>
                                        </button>
                                        <button
                                            type={'button'}
                                            className={'btn btn-link'}
                                            disabled={idx >= (form.workflowHome.resources.length - 1) || isSavingWorkflowHome}
                                            onClick={() => {
                                                saveWorkflowHome({
                                                    ...form.workflowHome,
                                                    resources: [
                                                        ...form?.workflowHome?.resources?.slice(0, idx),
                                                        form.workflowHome.resources[idx + 1],
                                                        form.workflowHome.resources[idx],
                                                        ...form?.workflowHome?.resources?.slice(idx + 2)
                                                    ]
                                                });
                                            }}
                                        >
                                            <i className="fas fa-arrow-down" style={{color: 'var(--abb-blue-primary)'}}/>
                                        </button>
                                    </div>
                                }
                                <a
                                    href={resource.url}
                                    className="list-group-item"
                                    target={resource.urlTarget}
                                >
                                    <span>{resource.name}</span>
                                    <i className={`${resource.icon} ml-1`}/>
                                </a>
                            </div>
                        ))}

                        {form.userCanViewGraphs &&
                            <Link to={`/dashboard/${form.id}`}
                                  className="list-group-item"
                            >
                                <span>Usage Graphs</span>
                                <i className="fas fa-chart-area ml-1"/>
                            </Link>
                        }

                        {form?.customDataforms?.length > 0 &&
                            <Link to={`/dataform/${form.id}/${form.customDataforms[0].id}`}
                                  className="list-group-item"
                            >
                                <span>Custom Reports</span>
                                <i className="fas fa-newspaper ml-1"/>
                            </Link>
                        }

                        {form?.userIsWfo &&
                            <Link
                                to={`/admin/assignee-maintenance/${form.id}`}
                                className="list-group-item"
                            >
                                <span>Workflow Owner Admin</span>
                                <i className="fas fa-user-cog ml-1"/>
                            </Link>
                        }

                        {form?.userIsWfo &&
                            <button
                                type={'button'}
                                className="list-group-item text-right"
                                disabled={isSavingWorkflowHome}
                                onClick={() => setAddResourceModalState(state => ({...state, show: true, resourceIndex: form?.workflowHome?.resources?.length || 0}))}
                            >
                                <span>Add new Resource</span>
                                <i className="fas fa-plus-circle ml-1"/>
                            </button>
                        }
                    </div>
                </div>
            </div>

            <div className={'row mt-4'}>
                <div className={'col-12 d-flex justify-content-start'} style={{gap: '10px'}}>
                    <Link
                        to={`/new/${form.id}`}
                        className={'btn btn-xl btn-primary'}
                    >
                        Start a New Request
                    </Link>
                    <Link
                        to={`/list/${form.id}`}
                        className={'btn btn-xl btn-secondary'}
                    >
                        Search All Requests
                    </Link>
                </div>
            </div>

            <div className={'row mt-4'}>
                <div className={'col-12'}>
                    <ul className="workflow-home-tabs nav nav-tabs">
                        {inbox !== undefined &&
                            <li className="nav-item">
                                <button
                                    type={'button'}
                                    className={`nav-link ${activeTab === 'inbox' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('inbox')}
                                >Pending with Me
                                </button>
                            </li>
                        }
                        {myRequests !== undefined &&
                            <li className="nav-item">
                                <button
                                    type={'button'}
                                    className={`nav-link ${activeTab === 'my-requests' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('my-requests')}
                                >My Open Requests
                                </button>
                            </li>
                        }
                    </ul>
                </div>
                {activeTab === 'inbox' &&
                    <div className={'col-12'}>
                        {inbox?.entries?.length > 0 ?
                            <DataTable
                                id="inbox-table"
                                striped={true}
                                classNames="table-borderless"
                                headings={inbox?.columns?.filter(c => ['id', 'entry_description', 'workflow_step', 'last_updated'].includes(c.prop))}
                                sortable={true}
                                setSort={setInboxSort}
                                sortState={inboxSort}
                                rows={inbox?.entries.sort(sortInboxEntries)}
                                onRowClick={(row: any) => {
                                    history.push('/entry/' + row.id);
                                }}
                            />
                            :
                            <>
                                {!inboxIsLoading && <div className={'d-flex justify-content-center mt-5 mb-4'}>No Requests Pending with Me</div>}
                            </>
                        }
                    </div>
                }
                {activeTab === 'my-requests' &&
                    <div className={'col-12'}>
                        {myRequestsIsLoading &&
                            <div className={'d-flex justify-content-center align-items-center'} style={{height: '150px'}}>
                                <i className={'fa fa-spin fa-2x fa-spinner'}/>
                                <span className={'ml-3'} style={{fontSize: '120%'}}>Loading...</span>
                            </div>
                        }
                        {myRequests?.entries?.length > 0 ?
                            <DataTable
                                id="my-requests-table"
                                striped={true}
                                classNames="table-borderless"
                                headings={myRequests.columns}
                                rows={myRequests.entries}
                                onRowClick={(row: any) => {
                                    history.push('/entry/' + row.id);
                                }}
                                cellRenderers={cellRenderers}
                            >
                                <Paginator
                                    currentPage={myRequests.page}
                                    rowsPerPage={myRequests.page_size}
                                    numberOfPages={myRequests.page_count}
                                    numberOfRecords={myRequests.total_count}
                                    onChange={(page: number, recordsPerPage: number) => {
                                        setMyRequestsSearchFilters({...myRequestsSearchFilters, page, page_size: recordsPerPage});
                                        setMyRequests({...myRequests, entries: []});
                                    }}
                                />
                            </DataTable>
                            :
                            <>
                                {!myRequestsIsLoading && <div className={'d-flex justify-content-center mt-5 mb-4'}>No Requests</div>}
                            </>
                        }
                    </div>
                }
            </div>

            {helpModalState?.show && <HelpModal close={() => showHelpModal(false)}/>}
            {addResourceModalState?.show &&
                <AddResourceModal
                    resource={addResourceModalState?.resource || {}}
                    close={() => setAddResourceModalState({...initialAddResourceModalState})}
                    save={(resource) => handleSaveResource(resource, addResourceModalState.resourceIndex)}
                />
            }
        </>
    );
};

const WorkflowOwnerCard = ({owner}) => {
    return (
        <>
            <h4>Workflow Owner</h4>
            <div className="contact-card-container">
                <div className="contact-card d-flex">
                    <div className="contact-avatar d-flex flex-column justify-content-center">
                        <img
                            src={`https://fletch.abb.com/profile-photo/${owner.email}`}
                            className="img-thumbnail rounded-circle"
                            style={{
                                width: '60px',
                                height: '60px',
                                padding: 0,
                                borderColor: '#aaa'
                            }}
                        />
                    </div>
                    <div className="contact-card-body d-flex flex-column justify-content-center">
                        {owner?.name && <h5 className="m-0 p-0">{owner.name}</h5>}
                        <div className="contact-card-contact-email">{owner.email}</div>
                        {owner?.title && <div className="contact-card-job-title">{owner.title}</div>}
                        {owner?.department && <div className="contact-card-company">{owner.department}</div>}
                    </div>
                </div>
            </div>
        </>
    )
}

const HelpModal = ({
                       close
                   }) => {
    return (
        <Modal size={'xl'}>
            <div className="modal-header">
                <h5 className="modal-title">Help</h5>
                <button type="button"
                        className="close"
                        tabIndex={-1}
                        onClick={() => close()}
                >×
                </button>
            </div>
            <div className="modal-body">
            </div>
        </Modal>
    )
}

const initalAddResourceFormState = {
    name: '',
    icon: '',
    type: '',
    url: '',
    urlTarget: ''
}

const resourceTypeOptions = [
    {label: 'Link', value: 'link'}
];

const resourceUrlTargetOptions = [
    {label: 'New Tab/Window', value: '_blank'},
    {label: 'Current Tab', value: '_self'}
];

const faIcons = [
    'fas fa-chess-pawn', 'fas fa-balance-scale', 'fas fa-map-pin', 'far fa-kiss-wink-heart', 'fas fa-bars', 'far fa-comment-alt', 'fas fa-portrait', 'fas fa-list-ol', 'fas fa-cloud-sun', 'fas fa-poo-storm', 'fab fa-xbox', 'fas fa-kaaba', 'fas fa-microscope', 'fas fa-rss', 'fas fa-hands-helping', 'fas fa-chess-bishop', 'fab fa-creative-commons-remix', 'fas fa-user-slash', 'fab fa-cc-amazon-pay', 'fas fa-bowling-ball', 'fas fa-sd-card', 'fas fa-user-tie', 'fas fa-fire-alt', 'far fa-caret-square-left', 'fab fa-npm', 'fab fa-creative-commons-share', 'fas fa-door-open', 'fab fa-skype', 'fas fa-teeth', 'fab fa-suse', 'fas fa-list-ul', 'fab fa-yarn', 'fas fa-pray', 'fab fa-line', 'fab fa-buysellads', 'fas fa-skiing-nordic', 'fab fa-napster', 'fas fa-street-view', 'fab fa-pied-piper', 'fas fa-ghost', 'fas fa-hiking', 'fas fa-book', 'fas fa-thermometer-quarter', 'fas fa-vote-yea', 'fab fa-diaspora', 'fas fa-th-large', 'fas fa-retweet', 'fas fa-chess-rook', 'fas fa-align-right', 'fas fa-columns', 'fas fa-robot', 'fas fa-mars-stroke-h', 'fas fa-sort', 'fab fa-goodreads-g', 'fab fa-steam', 'fas fa-battery-quarter', 'fab fa-etsy', 'fab fa-jedi-order', 'fas fa-binoculars', 'fas fa-tenge', 'fas fa-hourglass-half', 'fab fa-glide-g', 'fas fa-text-height', 'far fa-sun', 'fas fa-toilet', 'fas fa-rainbow', 'fas fa-arrows-alt-v', 'fas fa-skull-crossbones', 'fas fa-vials', 'fas fa-helicopter', 'fas fa-underline', 'fas fa-cloud-showers-heavy', 'fas fa-utensil-spoon', 'fab fa-invision', 'fab fa-font-awesome', 'fab fa-medium', 'fab fa-medrt', 'fab fa-grunt', 'fas fa-sms', 'fab fa-shirtsinbulk', 'fas fa-less-than-equal', 'fab fa-viadeo', 'fas fa-hand-holding-heart', 'fas fa-share', 'fas fa-user-plus', 'fab fa-goodreads', 'fab fa-maxcdn', 'fab fa-y-combinator', 'fab fa-internet-explorer', 'fab fa-pied-piper-pp', 'fas fa-step-backward', 'fas fa-satellite-dish', 'far fa-grin-tears', 'fab fa-waze', 'fab fa-mastodon', 'far fa-hand-paper', 'fas fa-glasses', 'fas fa-temperature-low', 'far fa-paper-plane', 'fas fa-check', 'fas fa-calendar-day', 'fas fa-thermometer-empty', 'fab fa-cc-diners-club', 'fas fa-car-side', 'fas fa-prescription-bottle-alt', 'fas fa-satellite', 'far fa-file-image', 'fab fa-codepen', 'fas fa-vr-cardboard', 'far fa-file-word', 'fas fa-vial', 'fab fa-gitlab', 'fas fa-comment-medical', 'fab fa-medium-m', 'fas fa-h-square', 'fas fa-theater-masks', 'fas fa-tools', 'fas fa-swimming-pool', 'fab fa-slideshare', 'fas fa-chevron-up', 'fas fa-paperclip', 'far fa-compass', 'fab fa-wix', 'fab fa-youtube', 'fas fa-swatchbook', 'fab fa-zhihu', 'fab fa-mandalorian', 'fas fa-golf-ball', 'fab fa-bluetooth', 'fas fa-city', 'fab fa-vimeo-v', 'fab fa-behance', 'fas fa-skiing', 'fas fa-dragon', 'fas fa-mercury', 'fab fa-font-awesome-alt', 'fas fa-grip-lines', 'fas fa-user-lock', 'far fa-user-circle', 'fas fa-car', 'far fa-images', 'fas fa-shield-alt', 'fab fa-stackpath', 'fas fa-syringe', 'fas fa-list', 'fas fa-file-invoice-dollar', 'fab fa-gitkraken', 'fas fa-shopping-basket', 'fab fa-creative-commons-nd', 'fas fa-ellipsis-v', 'fab fa-weibo', 'far fa-hand-spock', 'fas fa-upload', 'fas fa-award', 'fas fa-cubes', 'fas fa-mail-bulk', 'fab fa-odnoklassniki-square', 'fas fa-phone-volume', 'fas fa-euro-sign', 'fas fa-audio-description', 'fas fa-not-equal', 'fas fa-tags', 'fab fa-ubuntu', 'fas fa-network-wired', 'fab fa-facebook', 'far fa-window-restore', 'fas fa-phone-slash', 'fab fa-js-square', 'fab fa-monero', 'fas fa-strikethrough', 'fas fa-search-dollar', 'fas fa-battery-full', 'fas fa-star-of-david', 'far fa-edit', 'fab fa-500px', 'fas fa-tree', 'fab fa-blogger-b', 'fas fa-dove', 'fas fa-minus-circle', 'fas fa-oil-can', 'fas fa-asterisk', 'fab fa-ravelry', 'fas fa-crop', 'fas fa-truck', 'fas fa-drum', 'fas fa-rss-square', 'fas fa-bicycle', 'fab fa-untappd', 'fas fa-stream', 'far fa-sticky-note', 'fas fa-microphone-alt-slash', 'fas fa-tooth', 'far fa-eye', 'fas fa-comment-slash', 'fab fa-yahoo', 'far fa-newspaper', 'fas fa-atlas', 'fab fa-jira', 'far fa-caret-square-up', 'fab fa-wpforms', 'fas fa-crown', 'fab fa-vimeo-square', 'fab fa-hackerrank', 'fas fa-gas-pump', 'fas fa-place-of-worship', 'fab fa-markdown', 'fas fa-ruler-combined', 'fab fa-cloudversify', 'fas fa-hippo', 'fas fa-table', 'fas fa-transgender-alt', 'fab fa-hips', 'fab fa-rebel', 'fas fa-calendar-week', 'fas fa-shapes', 'fas fa-door-closed', 'fab fa-lyft', 'fas fa-poop', 'fab fa-servicestack', 'fas fa-ethernet', 'fas fa-socks', 'fab fa-github-alt', 'fab fa-facebook-square', 'fab fa-gofore', 'fas fa-cocktail', 'fab fa-font-awesome-logo-full', 'fab fa-itch-io', 'fas fa-equals', 'far fa-file-pdf', 'far fa-caret-square-right', 'fas fa-star-and-crescent', 'fas fa-laptop-medical', 'fab fa-the-red-yeti', 'fas fa-camera-retro', 'far fa-file-archive', 'fab fa-wordpress', 'fas fa-box-open', 'fab fa-stack-exchange', 'fab fa-nutritionix', 'fas fa-expand', 'fab fa-opencart', 'fas fa-dice-six', 'fab fa-pushed', 'fas fa-tty', 'fas fa-quote-right', 'fab fa-typo3', 'far fa-stop-circle', 'fab fa-sellcast', 'fas fa-tint', 'fab fa-bity', 'far fa-keyboard', 'fas fa-truck-loading', 'fas fa-level-up-alt', 'fas fa-dice', 'fab fa-soundcloud', 'far fa-minus-square', 'fas fa-plane', 'fab fa-java', 'fas fa-step-forward', 'fab fa-draft2digital', 'fas fa-wallet', 'fab fa-adobe', 'fas fa-chalkboard', 'far fa-image', 'fab fa-usb', 'far fa-envelope-open', 'fas fa-link', 'fab fa-sticker-mule', 'fas fa-sort-amount-down', 'fas fa-bacon', 'fas fa-first-aid', 'fas fa-grip-lines-vertical', 'fas fa-ribbon', 'fas fa-store', 'fab fa-dropbox', 'far fa-grin-squint', 'fas fa-people-carry', 'fas fa-shoe-prints', 'fas fa-skating', 'fas fa-truck-moving', 'far fa-comment', 'fab fa-mizuni', 'fab fa-amilia', 'fab fa-free-code-camp', 'fas fa-weight', 'fas fa-walking', 'fas fa-shopping-cart', 'fas fa-sign', 'fas fa-wheelchair', 'fas fa-industry', 'fas fa-share-alt', 'fas fa-mars-stroke', 'fas fa-vector-square', 'fab fa-joomla', 'fas fa-i-cursor', 'fab fa-css3', 'fab fa-reacteurope', 'fas fa-car-crash', 'fas fa-graduation-cap', 'far fa-list-alt', 'fas fa-igloo', 'fab fa-php', 'fab fa-meetup', 'fas fa-ship', 'fas fa-volume-mute', 'fab fa-red-river', 'fas fa-shuttle-van', 'fas fa-forward', 'far fa-snowflake', 'fab fa-uniregistry', 'fas fa-fill-drip', 'fab fa-facebook-f', 'fab fa-wpbeginner', 'fas fa-mosque', 'fas fa-circle-notch', 'fas fa-deaf', 'fas fa-pen-nib', 'far fa-grin-stars', 'fas fa-house-damage', 'far fa-check-circle', 'fab fa-blogger', 'fab fa-creative-commons-pd', 'fas fa-drafting-compass', 'fas fa-stethoscope', 'fab fa-d-and-d-beyond', 'fas fa-mask', 'fas fa-chart-pie', 'fas fa-pizza-slice', 'fab fa-firstdraft', 'fas fa-fire-extinguisher', 'fab fa-apper', 'fas fa-hard-hat', 'fab fa-deviantart', 'fas fa-hamsa', 'fas fa-utensils', 'fas fa-cloud-meatball', 'fab fa-docker', 'fab fa-weebly', 'fas fa-crosshairs', 'fab fa-centos', 'far fa-thumbs-down', 'fas fa-hammer', 'fas fa-pencil-ruler', 'fab fa-creative-commons-sampling', 'fas fa-laptop-code', 'fas fa-chess-king', 'fas fa-neuter', 'fas fa-align-justify', 'fas fa-podcast', 'fab fa-teamspeak', 'fab fa-gg-circle', 'fas fa-chalkboard-teacher', 'fas fa-shower', 'far fa-star', 'fab fa-linux', 'fas fa-trophy', 'fab fa-adversal', 'fab fa-cloudscale', 'fas fa-comment-dollar', 'far fa-object-group', 'fas fa-clinic-medical', 'fas fa-italic', 'fas fa-couch', 'fab fa-kaggle', 'fas fa-dice-d20', 'fas fa-fast-forward', 'fas fa-wifi', 'fas fa-chevron-down', 'fas fa-recycle', 'fas fa-map-marked', 'fas fa-grip-horizontal', 'fas fa-signal', 'fas fa-stop', 'fab fa-black-tie', 'fas fa-laptop', 'fas fa-cloud-sun-rain', 'fas fa-external-link-square-alt', 'fab fa-ethereum', 'fas fa-dice-three', 'far fa-folder', 'far fa-copy', 'fas fa-cheese', 'fab fa-weixin', 'fab fa-google', 'fas fa-flag-usa', 'fas fa-icicles', 'fas fa-level-down-alt', 'fas fa-video', 'fas fa-money-bill-wave-alt', 'fab fa-gulp', 'far fa-id-card', 'fas fa-ticket-alt', 'fas fa-chevron-circle-up', 'fab fa-autoprefixer', 'fas fa-thermometer-three-quarters', 'fas fa-chevron-circle-down', 'fas fa-home', 'far fa-bell-slash', 'fas fa-thermometer', 'fas fa-venus-mars', 'fas fa-microphone', 'fas fa-compress-arrows-alt', 'fab fa-cc-paypal', 'fas fa-hot-tub', 'fas fa-reply-all', 'far fa-hand-rock', 'fas fa-horse-head', 'far fa-closed-captioning', 'fas fa-ambulance', 'fas fa-caret-left', 'fas fa-campground', 'fab fa-cc-visa', 'far fa-circle', 'fas fa-bath', 'fab fa-bluetooth-b', 'fas fa-rocket', 'fab fa-fly', 'fab fa-uber', 'fas fa-file-prescription', 'fas fa-mountain', 'fas fa-cat', 'fas fa-braille', 'fab fa-trello', 'fas fa-mobile-alt', 'fas fa-tram', 'far fa-frown', 'fab fa-supple', 'fas fa-suitcase', 'far fa-arrow-alt-circle-down', 'fas fa-torii-gate', 'far fa-hand-point-down', 'fab fa-quinscape', 'fas fa-church', 'fas fa-eject', 'far fa-grin-beam', 'fas fa-download', 'fas fa-paint-brush', 'fab fa-ebay', 'fas fa-feather', 'far fa-arrow-alt-circle-right', 'fas fa-angle-double-right', 'far fa-bell', 'far fa-surprise', 'fas fa-sync', 'far fa-square', 'fas fa-search-minus', 'fab fa-themeisle', 'fab fa-gg', 'fas fa-shopping-bag', 'fas fa-piggy-bank', 'fas fa-stamp', 'fas fa-feather-alt', 'far fa-smile-beam', 'fas fa-compact-disc', 'fab fa-accusoft', 'fas fa-diagnoses', 'fas fa-prescription-bottle', 'fas fa-university', 'fas fa-ankh', 'fas fa-drumstick-bite', 'fab fa-chromecast', 'fab fa-steam-square', 'far fa-smile', 'far fa-life-ring', 'fab fa-flipboard', 'fab fa-openid', 'fas fa-stopwatch', 'fas fa-child', 'fab fa-get-pocket', 'fab fa-xing', 'fas fa-money-bill', 'fab fa-dev', 'fas fa-smoking-ban', 'fab fa-jenkins', 'fab fa-hornbill', 'fas fa-file-download', 'fas fa-compress', 'fas fa-bone', 'fas fa-stroopwafel', 'fas fa-envelope-open-text', 'fas fa-exchange-alt', 'fas fa-venus-double', 'fab fa-creative-commons-nc-jp', 'fab fa-leanpub', 'fab fa-superpowers', 'fas fa-won-sign', 'fas fa-calculator', 'fab fa-delicious', 'far fa-comment-dots', 'fas fa-table-tennis', 'fas fa-camera', 'fas fa-map-marker-alt', 'fab fa-pied-piper-hat', 'fas fa-sync-alt', 'fas fa-capsules', 'fas fa-heartbeat', 'fab fa-fulcrum', 'fas fa-seedling', 'far fa-calendar-times', 'far fa-calendar-check', 'fas fa-book-reader', 'fas fa-signature', 'far fa-calendar-minus', 'far fa-file-audio', 'fas fa-beer', 'fab fa-d-and-d', 'fas fa-plus', 'fab fa-fedora', 'fas fa-cannabis', 'far fa-times-circle', 'fas fa-paw', 'fas fa-dharmachakra', 'far fa-comments', 'fas fa-candy-cane', 'fas fa-truck-monster', 'fas fa-republican', 'fas fa-file-export', 'fas fa-blind', 'fas fa-microphone-alt', 'far fa-bookmark', 'fas fa-server', 'fab fa-git-alt', 'fab fa-rocketchat', 'fab fa-critical-role', 'fas fa-peace', 'fab fa-symfony', 'fab fa-centercode', 'fas fa-phone-square', 'fas fa-info-circle', 'fas fa-cloud-moon-rain', 'fas fa-users', 'fas fa-language', 'fas fa-coffee', 'fas fa-file-contract', 'fas fa-tablet-alt', 'fas fa-horse', 'fab fa-creative-commons', 'fab fa-old-republic', 'fas fa-leaf', 'fas fa-sort-alpha-up', 'far fa-hand-pointer', 'fas fa-allergies', 'fas fa-angle-right', 'fas fa-briefcase', 'fab fa-twitter-square', 'fas fa-running', 'fas fa-digital-tachograph', 'fas fa-highlighter', 'fab fa-fort-awesome-alt', 'fas fa-hamburger', 'far fa-handshake', 'fab fa-git', 'fas fa-poo', 'fas fa-gavel', 'fas fa-business-time', 'fas fa-chart-area', 'fas fa-box', 'fab fa-angular', 'fas fa-thermometer-half', 'fab fa-google-plus-g', 'fas fa-bible', 'fas fa-user-check', 'fas fa-terminal', 'fas fa-chevron-right', 'fas fa-spider', 'fas fa-search-location', 'fas fa-ice-cream', 'fab fa-telegram', 'fas fa-pen-square', 'fas fa-flag-checkered', 'fas fa-glass-martini-alt', 'fas fa-redo', 'fab fa-kickstarter', 'fab fa-wikipedia-w', 'fab fa-salesforce', 'fas fa-quran', 'far fa-gem', 'fab fa-elementor', 'fab fa-accessible-icon', 'fas fa-baby', 'fas fa-arrow-circle-right', 'fas fa-user-nurse', 'far fa-dizzy', 'fab fa-snapchat-ghost', 'fab fa-linkedin-in', 'fas fa-khanda', 'fab fa-amazon', 'fab fa-btc', 'fas fa-hryvnia', 'fab fa-twitter', 'fas fa-american-sign-language-interpreting', 'fas fa-charging-station', 'fab fa-fonticons', 'fab fa-nimblr', 'fas fa-chevron-circle-left', 'fas fa-music', 'fab fa-phoenix-squadron', 'fab fa-sourcetree', 'fab fa-node', 'fas fa-exclamation', 'far fa-grin-beam-sweat', 'fab fa-intercom', 'fab fa-linkedin', 'fab fa-wordpress-simple', 'fas fa-align-left', 'far fa-kiss-beam', 'fab fa-yammer', 'fab fa-dribbble', 'fas fa-location-arrow', 'fas fa-shekel-sign', 'fas fa-coins', 'fas fa-file-invoice', 'fas fa-procedures', 'fas fa-user-injured', 'fab fa-studiovinari', 'fab fa-telegram-plane', 'fas fa-film', 'fab fa-schlix', 'fas fa-restroom', 'fas fa-cross', 'fas fa-phone', 'fab fa-bootstrap', 'fas fa-angle-up', 'fas fa-parking', 'fas fa-concierge-bell', 'fas fa-subway', 'fas fa-user-times', 'fab fa-digg', 'fas fa-splotch', 'fas fa-traffic-light', 'fab fa-speakap', 'far fa-user', 'fab fa-android', 'fas fa-tshirt', 'fab fa-hotjar', 'fas fa-bus-alt', 'fas fa-chess-queen', 'fab fa-reddit', 'fas fa-cloud', 'far fa-hand-point-up', 'fas fa-fire', 'fab fa-r-project', 'fas fa-unlock', 'fab fa-affiliatetheme', 'fas fa-square-root-alt', 'fas fa-bolt', 'fas fa-broadcast-tower', 'fab fa-dhl', 'far fa-hand-point-left', 'fas fa-toilet-paper', 'far fa-meh', 'fas fa-print', 'fab fa-gripfire', 'fab fa-alipay', 'fas fa-volleyball-ball', 'far fa-address-card', 'fas fa-holly-berry', 'fas fa-sign-in-alt', 'far fa-laugh', 'fas fa-apple-alt', 'fas fa-search-plus', 'fab fa-python', 'fas fa-mobile', 'fab fa-itunes', 'fab fa-yoast', 'far fa-file-alt', 'fas fa-dolly', 'fas fa-gopuram', 'fas fa-ruler-horizontal', 'fas fa-user-alt-slash', 'fab fa-mixcloud', 'fas fa-chevron-left', 'fab fa-jsfiddle', 'fab fa-odnoklassniki', 'fas fa-ruble-sign', 'fab fa-ioxhost', 'fas fa-globe', 'fab fa-quora', 'fab fa-twitch', 'fas fa-id-card-alt', 'fas fa-genderless', 'fab fa-renren', 'fas fa-arrows-alt', 'fas fa-bullseye', 'fas fa-superscript', 'fas fa-praying-hands', 'far fa-lightbulb', 'fas fa-spray-can', 'far fa-heart', 'fas fa-battery-three-quarters', 'fas fa-archway', 'fas fa-directions', 'fas fa-puzzle-piece', 'fas fa-dog', 'fab fa-blackberry', 'fab fa-forumbee', 'fab fa-resolving', 'fas fa-chair', 'fab fa-stripe', 'fab fa-flickr', 'far fa-save', 'fab fa-creative-commons-zero', 'fab fa-paypal', 'fab fa-creative-commons-nc-eu', 'far fa-caret-square-down', 'fas fa-umbrella-beach', 'fab fa-sass', 'fas fa-th', 'fab fa-cc-mastercard', 'fab fa-angellist', 'far fa-arrow-alt-circle-left', 'fas fa-paragraph', 'fas fa-thermometer-full', 'fas fa-undo', 'fab fa-atlassian', 'fas fa-fast-backward', 'fas fa-quidditch', 'fas fa-hand-holding-usd', 'fas fa-mars', 'fab fa-rockrms', 'fas fa-arrows-alt-h', 'fas fa-donate', 'fas fa-backspace', 'fas fa-toggle-on', 'fas fa-angle-left', 'fas fa-subscript', 'fas fa-outdent', 'fas fa-ruler-vertical', 'fab fa-google-plus-square', 'fas fa-trash', 'fab fa-hacker-news', 'fas fa-pallet', 'fab fa-steam-symbol', 'fab fa-wpressr', 'fas fa-tv', 'fas fa-prescription', 'fas fa-tasks', 'fas fa-medkit', 'far fa-grin-wink', 'fab fa-github', 'fas fa-headphones', 'fas fa-atom', 'fas fa-long-arrow-alt-down', 'fab fa-rev', 'fas fa-paste', 'fab fa-dochub', 'fas fa-yen-sign', 'fas fa-heading', 'fab fa-instagram', 'far fa-copyright', 'fab fa-safari', 'fas fa-hat-wizard', 'fas fa-pause', 'fab fa-cuttlefish', 'fas fa-caret-right', 'fab fa-galactic-senate', 'far fa-hand-scissors', 'fab fa-deploydog', 'fas fa-user-md', 'fab fa-chrome', 'fas fa-suitcase-rolling', 'fas fa-shipping-fast', 'fas fa-align-center', 'far fa-calendar-alt', 'fas fa-user-minus', 'far fa-grin-alt', 'fas fa-greater-than-equal', 'fas fa-pepper-hot', 'fas fa-code', 'fab fa-trade-federation', 'fas fa-user-friends', 'fab fa-keycdn', 'fas fa-clipboard-check', 'fab fa-yandex', 'fas fa-baseball-ball', 'fas fa-hanukiah', 'fas fa-band-aid', 'fas fa-eye-dropper', 'fas fa-biohazard', 'fas fa-bullhorn', 'fas fa-info', 'fab fa-cloudsmith', 'fas fa-clipboard-list', 'fas fa-monument', 'fas fa-tractor', 'fas fa-video-slash', 'fas fa-pen', 'fas fa-star-of-life', 'fas fa-tachometer-alt', 'far fa-kiss', 'fas fa-tint-slash', 'fas fa-basketball-ball', 'fab fa-dribbble-square', 'fas fa-minus', 'fas fa-air-freshener', 'fas fa-cash-register', 'fas fa-dice-five', 'fab fa-phoenix-framework', 'fas fa-vihara', 'fas fa-star-half-alt', 'fas fa-map-marker', 'fab fa-react', 'fas fa-angle-down', 'fas fa-gamepad', 'fab fa-readme', 'fas fa-cut', 'fas fa-hospital-alt', 'fab fa-ember', 'fab fa-audible', 'fas fa-draw-polygon', 'fab fa-playstation', 'fas fa-smog', 'fas fa-glass-cheers', 'fab fa-freebsd', 'fas fa-pager', 'fab fa-buffer', 'fas fa-plus-circle', 'fas fa-share-alt-square', 'fas fa-dna', 'fas fa-cookie-bite', 'fab fa-apple-pay', 'fas fa-text-width', 'fas fa-plane-arrival', 'far fa-address-book', 'fab fa-whatsapp-square', 'fab fa-adn', 'fab fa-reddit-square', 'far fa-grin-hearts', 'fas fa-radiation-alt', 'fas fa-font', 'fas fa-cart-arrow-down', 'fas fa-qrcode', 'fas fa-route', 'fas fa-file-medical-alt', 'far fa-hourglass', 'fab fa-gratipay', 'fab fa-whatsapp', 'far fa-arrow-alt-circle-up', 'fas fa-democrat', 'fas fa-cloud-upload-alt', 'fab fa-acquisitions-incorporated', 'fas fa-toolbox', 'fas fa-dumbbell', 'far fa-folder-open', 'fas fa-database', 'far fa-hospital', 'far fa-file-excel', 'fas fa-brush', 'fas fa-poll-h', 'fas fa-battery-empty', 'fab fa-searchengin', 'fab fa-phabricator', 'fas fa-grip-vertical', 'fas fa-filter', 'fab fa-dashcube', 'far fa-file-code', 'fas fa-arrow-circle-down', 'fas fa-play', 'fas fa-arrow-left', 'fab fa-pinterest-square', 'fas fa-angle-double-up', 'fas fa-menorah', 'fab fa-pinterest', 'far fa-grin-squint-tears', 'fab fa-vk', 'far fa-chart-bar', 'fas fa-mug-hot', 'fas fa-mars-double', 'far fa-credit-card', 'fab fa-hubspot', 'fab fa-creative-commons-sa', 'fas fa-history', 'fas fa-user-cog', 'fas fa-book-open', 'fas fa-gift', 'fas fa-money-check-alt', 'fab fa-bandcamp', 'far fa-meh-blank', 'fas fa-x-ray', 'fab fa-node-js', 'fas fa-less-than', 'fas fa-mortar-pestle', 'far fa-hdd', 'fas fa-water', 'fab fa-neos', 'fas fa-fingerprint', 'fab fa-aviato', 'fab fa-megaport', 'fab fa-first-order-alt', 'far fa-play-circle', 'fas fa-snowplow', 'fab fa-creative-commons-by', 'far fa-calendar-plus', 'fas fa-user-graduate', 'fas fa-angle-double-left', 'fas fa-at', 'fab fa-battle-net', 'far fa-hand-point-right', 'fas fa-pencil-alt', 'fas fa-hotel', 'fas fa-map-marked-alt', 'fab fa-fantasy-flight-games', 'fas fa-desktop', 'far fa-clipboard', 'fas fa-mouse-pointer', 'fab fa-cc-discover', 'fas fa-user-tag', 'fab fa-fonticons-fi', 'fas fa-sort-up', 'fab fa-palfed', 'far fa-dot-circle', 'fas fa-crutch', 'far fa-trash-alt', 'fas fa-divide', 'fas fa-wave-square', 'fab fa-stripe-s', 'far fa-calendar', 'fas fa-umbrella', 'fab fa-cc-stripe', 'fas fa-palette', 'fab fa-optin-monster', 'fab fa-vine', 'fas fa-infinity', 'fas fa-chess-knight', 'fas fa-headset', 'fas fa-money-check', 'fas fa-guitar', 'fas fa-inbox', 'fas fa-user-clock', 'fab fa-firefox', 'fas fa-marker', 'fas fa-ring', 'fas fa-truck-pickup', 'fas fa-boxes', 'fas fa-cube', 'fas fa-solar-panel', 'fas fa-heart-broken', 'fas fa-envelope-square', 'far fa-tired', 'fas fa-receipt', 'fas fa-yin-yang', 'fas fa-user-edit', 'fas fa-drum-steelpan', 'far fa-hand-peace', 'fab fa-grav', 'fas fa-chart-line', 'fab fa-algolia', 'fas fa-cogs', 'fab fa-squarespace', 'fas fa-sort-numeric-down', 'fas fa-person-booth', 'fas fa-spinner', 'fab fa-foursquare', 'fab fa-fort-awesome', 'fas fa-adjust', 'fas fa-blender', 'fas fa-taxi', 'fas fa-rupee-sign', 'far fa-pause-circle', 'fab fa-tencent-weibo', 'fab fa-ussunnah', 'fab fa-lastfm', 'fas fa-folder-plus', 'fas fa-file-import', 'fas fa-hourglass-end', 'fab fa-shopware', 'fab fa-js', 'fas fa-hockey-puck', 'fas fa-angle-double-down', 'fas fa-dice-d6', 'fab fa-expeditedssl', 'fas fa-arrow-right', 'fab fa-bitcoin', 'fas fa-bezier-curve', 'fab fa-facebook-messenger', 'fas fa-cloud-rain', 'fab fa-codiepie', 'fas fa-medal', 'fas fa-cloud-download-alt', 'fab fa-vnv', 'fab fa-ello', 'fas fa-check-double', 'fas fa-bold', 'fas fa-ban', 'fab fa-ns8', 'fas fa-hotdog', 'fas fa-fighter-jet', 'fas fa-chevron-circle-right', 'fab fa-css3-alt', 'fab fa-cc-apple-pay', 'fab fa-vimeo', 'fas fa-long-arrow-alt-left', 'fab fa-think-peaks', 'fab fa-staylinked', 'far fa-flushed', 'far fa-window-close', 'fas fa-om', 'fab fa-google-play', 'fas fa-comments-dollar', 'fab fa-artstation', 'fab fa-penny-arcade', 'fas fa-transgender', 'fab fa-periscope', 'fas fa-brain', 'fab fa-viber', 'fab fa-themeco', 'fab fa-tumblr', 'fas fa-volume-up', 'far fa-meh-rolling-eyes', 'fas fa-square-full', 'fab fa-opera', 'fas fa-pen-fancy', 'fas fa-sliders-h', 'fas fa-percent', 'far fa-moon', 'far fa-building', 'fab fa-bimobject', 'fas fa-hand-middle-finger', 'fas fa-user-shield', 'fab fa-glide', 'fab fa-imdb', 'fas fa-flask', 'fab fa-aws', 'fab fa-qq', 'far fa-grin', 'fab fa-sellsy', 'fab fa-creative-commons-nc', 'fas fa-redo-alt', 'fab fa-pinterest-p', 'fab fa-microsoft', 'fab fa-digital-ocean', 'fas fa-briefcase-medical', 'fab fa-viacoin', 'fas fa-ellipsis-h', 'fas fa-lock-open', 'fas fa-weight-hanging', 'fas fa-magnet', 'fas fa-landmark', 'fas fa-power-off', 'fab fa-skyatlas', 'fab fa-sketch', 'fas fa-times', 'fab fa-dyalog', 'fas fa-magic', 'fas fa-broom', 'fas fa-volume-off', 'fas fa-mars-stroke-v', 'fas fa-hospital-symbol', 'fas fa-jedi', 'fas fa-temperature-high', 'fas fa-wine-glass-alt', 'fas fa-cog', 'fas fa-dice-one', 'far fa-plus-square', 'fas fa-glass-whiskey', 'fas fa-haykal', 'fas fa-random', 'far fa-share-square', 'fas fa-question', 'fab fa-evernote', 'far fa-laugh-squint', 'fas fa-hand-holding', 'fab fa-mendeley', 'fas fa-synagogue', 'fas fa-fill', 'fab fa-discord', 'fab fa-raspberry-pi', 'fab fa-figma', 'fas fa-user-astronaut', 'fab fa-korvue', 'fab fa-avianex', 'fab fa-hacker-news-square', 'fas fa-sign-language', 'fas fa-dollar-sign', 'fas fa-pills', 'fab fa-whmcs', 'fab fa-google-wallet', 'far fa-question-circle', 'fas fa-cookie', 'fas fa-low-vision', 'fas fa-pen-alt', 'fab fa-replyd', 'fab fa-youtube-square', 'fas fa-sleigh', 'fas fa-screwdriver', 'fas fa-dumpster-fire', 'fab fa-cpanel', 'far fa-file-video', 'fas fa-dumpster', 'fas fa-globe-asia', 'fas fa-mitten', 'fab fa-osi', 'fas fa-wind', 'fas fa-plug', 'fas fa-passport', 'fab fa-deskpro', 'fas fa-hourglass-start', 'fas fa-space-shuttle', 'far fa-smile-wink', 'fas fa-indent', 'far fa-flag', 'fas fa-bug', 'fas fa-headphones-alt', 'fab fa-viadeo-square', 'fas fa-glass-martini', 'fab fa-linode', 'fas fa-poll', 'fas fa-thumbtack', 'fas fa-birthday-cake', 'fas fa-pound-sign', 'fas fa-key', 'fas fa-trash-restore', 'fas fa-bong', 'fas fa-volume-down', 'fas fa-quote-left', 'fab fa-gitter', 'fab fa-tripadvisor', 'fas fa-blog', 'fab fa-bitbucket', 'fas fa-burn', 'fab fa-amazon-pay', 'fas fa-meteor', 'fas fa-microphone-slash', 'fas fa-wine-glass', 'fas fa-file-upload', 'far fa-grimace', 'fab fa-cc-jcb', 'fas fa-user-secret', 'fab fa-wizards-of-the-coast', 'far fa-grin-tongue-squint', 'fab fa-laravel', 'fas fa-tablets', 'fas fa-tablet', 'fas fa-battery-half', 'fab fa-erlang', 'far fa-window-minimize', 'fas fa-archive', 'fas fa-bus', 'fab fa-kickstarter-k', 'fab fa-hooli', 'fas fa-trademark', 'fab fa-canadian-maple-leaf', 'fab fa-medapps', 'far fa-laugh-wink', 'fas fa-wine-bottle', 'fas fa-train', 'fab fa-earlybirds', 'fab fa-product-hunt', 'fab fa-fedex', 'fas fa-percentage', 'fab fa-page4', 'fas fa-ruler', 'fas fa-radiation', 'fab fa-apple', 'fab fa-snapchat', 'far fa-angry', 'fab fa-hire-a-helper', 'fas fa-dice-four', 'fas fa-blender-phone', 'fas fa-venus', 'fab fa-pied-piper-alt', 'fab fa-asymmetrik', 'fas fa-frog', 'fas fa-wrench', 'fas fa-th-list', 'fas fa-project-diagram', 'fas fa-book-dead', 'fas fa-caret-up', 'fab fa-spotify', 'fas fa-dungeon', 'fas fa-sitemap', 'fas fa-car-battery', 'fas fa-dice-two', 'fas fa-joint', 'fas fa-tape', 'fas fa-code-branch', 'fas fa-crow', 'fab fa-empire', 'fab fa-tumblr-square', 'fab fa-airbnb', 'fas fa-egg', 'fab fa-galactic-republic', 'fab fa-stumbleupon', 'fas fa-snowboarding', 'fab fa-houzz', 'fas fa-pastafarianism', 'fas fa-smoking', 'fas fa-toggle-off', 'fas fa-search', 'fas fa-external-link-alt', 'fas fa-file-signature', 'fas fa-globe-europe', 'fas fa-user-alt', 'fab fa-redhat', 'fas fa-chess-board', 'fas fa-users-cog', 'fab fa-speaker-deck', 'fab fa-connectdevelop', 'fab fa-researchgate', 'fab fa-less', 'far fa-id-badge', 'fas fa-memory', 'far fa-futbol', 'fas fa-sort-numeric-up', 'fas fa-arrow-circle-left', 'fas fa-eraser', 'fas fa-sort-alpha-down', 'fas fa-lock', 'fas fa-cart-plus', 'fas fa-store-alt', 'fas fa-snowman', 'fas fa-otter', 'fas fa-backward', 'fab fa-joget', 'fas fa-hashtag', 'far fa-check-square', 'fas fa-male', 'fab fa-edge', 'fab fa-cc-amex', 'fas fa-unlock-alt', 'fas fa-arrow-down', 'fas fa-exclamation-circle', 'fab fa-app-store', 'fab fa-pagelines', 'fab fa-slack', 'fas fa-crop-alt', 'fab fa-windows', 'fab fa-google-plus', 'fab fa-buromobelexperte', 'fas fa-bread-slice', 'fab fa-keybase', 'fas fa-fax', 'fas fa-tag', 'far fa-eye-slash', 'fab fa-confluence', 'fab fa-envira', 'fas fa-layer-group', 'fab fa-magento', 'fas fa-long-arrow-alt-right', 'fab fa-usps', 'fas fa-folder-minus', 'fas fa-lira-sign', 'fas fa-motorcycle', 'fab fa-snapchat-square', 'fab fa-lastfm-square', 'fab fa-slack-hash', 'fab fa-drupal', 'fas fa-road', 'fas fa-football-ball', 'fas fa-unlink', 'fas fa-trash-restore-alt', 'far fa-sad-tear', 'fas fa-journal-whills', 'fab fa-app-store-ios', 'fas fa-file-csv', 'fas fa-bed', 'fab fa-creative-commons-sampling-plus', 'fas fa-swimmer', 'far fa-file-powerpoint', 'fab fa-wpexplorer', 'fab fa-mix', 'fas fa-torah', 'far fa-envelope', 'fas fa-female', 'fab fa-simplybuilt', 'fab fa-mailchimp', 'far fa-sad-cry', 'fas fa-certificate', 'far fa-grin-tongue-wink', 'far fa-frown-open', 'fab fa-git-square', 'fab fa-behance-square', 'fab fa-vuejs', 'fas fa-money-bill-wave', 'fas fa-map-signs', 'fab fa-google-drive', 'far fa-clock', 'fas fa-carrot', 'fab fa-font-awesome-flag', 'fas fa-notes-medical', 'fab fa-html5', 'fab fa-sistrix', 'fab fa-stack-overflow', 'far fa-registered', 'fab fa-strava', 'fas fa-sim-card', 'far fa-window-maximize', 'fas fa-barcode', 'fas fa-dolly-flatbed', 'fas fa-funnel-dollar', 'fas fa-user-ninja', 'fas fa-anchor', 'fas fa-bomb', 'far fa-laugh-beam', 'fas fa-skull', 'fab fa-uikit', 'fas fa-reply', 'fas fa-paint-roller', 'fab fa-github-square', 'far fa-map', 'fas fa-fist-raised', 'fab fa-itunes-note', 'fas fa-ad', 'fas fa-spa', 'fas fa-book-medical', 'fas fa-universal-access', 'fas fa-greater-than', 'fas fa-arrow-circle-up', 'fab fa-contao', 'fas fa-slash', 'fas fa-gifts', 'fab fa-first-order', 'fab fa-creative-commons-pd-alt', 'fab fa-yandex-international', 'fas fa-caret-down', 'far fa-clone', 'far fa-file', 'fab fa-vaadin', 'fas fa-plane-departure', 'fas fa-undo-alt', 'fab fa-stumbleupon-circle', 'fas fa-luggage-cart', 'fas fa-kiwi-bird', 'fas fa-globe-africa', 'fas fa-arrow-up', 'far fa-object-ungroup', 'fas fa-assistive-listening-systems', 'fas fa-long-arrow-alt-up', 'fab fa-modx', 'far fa-hand-lizard', 'fab fa-discourse', 'far fa-thumbs-up', 'fas fa-baby-carriage', 'fas fa-exclamation-triangle', 'fab fa-nintendo-switch', 'fab fa-yelp', 'fas fa-fish', 'fas fa-microchip', 'far fa-money-bill-alt', 'fas fa-teeth-open', 'fas fa-sort-down', 'fas fa-hands', 'fas fa-school', 'fab fa-patreon', 'far fa-star-half', 'fas fa-sort-amount-up', 'fas fa-expand-arrows-alt', 'fab fa-ups', 'fas fa-file-medical', 'fas fa-scroll', 'fab fa-angrycreative', 'fab fa-sith', 'fab fa-scribd', 'fab fa-xing-square', 'fas fa-chess', 'fas fa-sign-out-alt', 'fas fa-cloud-moon', 'fas fa-parachute-box', 'fab fa-wolf-pack-battalion', 'fas fa-warehouse', 'far fa-grin-tongue', 'far fa-lemon', 'fas fa-car-alt', 'fas fa-globe-americas', 'fab fa-reddit-alien'
];

const AddResourceModal = ({
                              resource,
                              close,
                              save
                          }) => {
    const [error, setError] = useState<string>('');
    const [formState, setFormState] = useState<any>({...initalAddResourceFormState, ...resource});
    const [showIconSearch, setShowIconSearch] = useState<boolean>(false);
    const iconSearchRef = useRef();
    const [iconFilterText, setIconFilterText] = useState<string>('');

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            // @ts-ignore
            if (iconSearchRef?.current && !iconSearchRef?.current?.contains(event.target)) {
                setShowIconSearch(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [iconSearchRef]);

    const handleSave = () => {
        setError('');

        // basic validation
        const validTypes = ['link'];

        if (!formState.name) {
            setError('A name is required');
            return;
        }
        if (!formState.icon) {
            setError('An icon is required');
            return;
        }
        if (!validTypes.includes(formState?.type || '')) {
            setError('No valid type selected');
            return;
        }
        if (formState.type === 'link') {
            if (!formState.url) {
                setError('A URL is required');
                return;
            }
            if (!formState.urlTarget) {
                setError('A target is required');
                return;
            }
        }

        save(formState);
    }

    const getOptionForType = (v) => {
        return resourceTypeOptions.find(o => o.value === v);
    }
    const getOptionForUrlTarget = (v) => {
        return resourceUrlTargetOptions.find(o => o.value === v);
    }

    return (
        <Modal size={'lg'}>
            <div className="modal-header">
                <h5 className="modal-title">Add Resource</h5>
                <button type="button"
                        className="close"
                        tabIndex={-1}
                        onClick={() => close()}
                >×
                </button>
            </div>
            <div className="modal-body">
                {error && <Alert type={'danger'} message={error}/>}

                <div className={'row'}>
                    <div className={'col-10'}>
                        <div className="form-group">
                            <label>Name</label>
                            <input
                                type={'text'}
                                className={'form-control'}
                                value={formState?.name}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setFormState(state => ({
                                        ...state,
                                        name: v
                                    }))
                                }}
                            />
                        </div>
                    </div>
                    <div className={'col-2'}>
                        <div className="form-group">
                            <label>Icon</label>
                            <div
                                onClick={() => setShowIconSearch(true)}
                                style={{
                                    width: '40px',
                                    height: '35px',
                                    padding: '4px',
                                    border: '1px solid #ccc'
                                }}
                            >
                                {formState?.icon && <i className={`fa-2x ${formState.icon}`}/>}
                            </div>
                            {showIconSearch &&
                                <div
                                    ref={iconSearchRef}
                                    style={{
                                        border: '1px solid #ccc',
                                        position: 'absolute',
                                        width: '200px',
                                        height: '250px',
                                        zIndex: 1,
                                        backgroundColor: 'white',
                                        overflowY: 'scroll'
                                    }}>
                                    <input
                                        type={'text'}
                                        className={'form-control'}
                                        placeholder={'Search Icons'}
                                        value={iconFilterText}
                                        onChange={(e) => setIconFilterText(e.target.value)}
                                    />
                                    <div className={'d-flex flex-column p-2'} style={{gap: '4px'}}>
                                        {faIcons
                                            .filter(icon => icon.includes(iconFilterText))
                                            .map(icon => (
                                                <div
                                                    onClick={() => {
                                                        setFormState(state => ({
                                                            ...state,
                                                            icon
                                                        }))
                                                        setShowIconSearch(false);
                                                    }}
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'row',
                                                        justifyContent: 'start',
                                                        alignItems: 'center',
                                                        gap: '4px',
                                                    }}
                                                >
                                                    <i className={`d-block fa-2x ${icon}`}/>
                                                    <small className={'d-block text-center'}>{icon.split(' ')[1].replace('fa-', '')}</small>
                                                </div>
                                            ))}
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </div>

                <div className={'row'}>
                    <div className={'col-12'}>
                        <div className="form-group">
                            <label>Type</label>
                            <ReactSelect
                                required={true}
                                onChange={(option) => {
                                    setFormState((state) => ({
                                        ...state,
                                        type: option?.value
                                    }));
                                }}
                                options={resourceTypeOptions}
                                defaultValue={null}
                                value={getOptionForType(formState?.type)}
                                placeholder=""
                                isClearable={true}
                            />
                        </div>
                    </div>
                </div>

                {formState?.type === 'link' &&
                    <>
                        <div className={'row'}>
                            <div className={'col-12'}>
                                <div className={'form-group'}>
                                    <label>URL</label>
                                    <input
                                        type={'text'}
                                        className={'form-control'}
                                        value={formState?.url}
                                        onChange={(e) => {
                                            const v = e.target.value;
                                            setFormState((state) => ({
                                                ...state,
                                                url: v
                                            }));
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={'row'}>
                            <div className={'col-12'}>
                                <div className={'form-group'}>
                                    <label>Target</label>
                                    <ReactSelect
                                        required={true}
                                        onChange={(option) => {
                                            setFormState((state) => ({
                                                ...state,
                                                urlTarget: option.value
                                            }));
                                        }}
                                        options={resourceUrlTargetOptions}
                                        defaultValue={null}
                                        value={getOptionForUrlTarget(formState?.urlTarget)}
                                        placeholder=""
                                        isClearable={true}
                                    />
                                </div>
                            </div>
                        </div>
                    </>
                }
            </div>
            <div className={'modal-footer'}>
                <button type="button"
                        className="btn btn-secondary"
                        onClick={close}
                >Cancel
                </button>
                <button type="button"
                        className="btn btn-primary"
                        onClick={handleSave}
                >Save
                </button>
            </div>
        </Modal>
    )
}